import React from "react";
import cn from "./helpers/cn";
import { ListContext, } from "./List";

export interface ListItemContextValue {
  dense: boolean;
  inset: boolean;
}

export const ListItemContext = React.createContext<ListItemContextValue>({
  dense: false,
  inset: false,
});

interface ListItemProps extends Omit<React.LiHTMLAttributes<HTMLLIElement>, "onClick"> {
  component?: React.ElementType;
  /** Quando true, renderiza como botão clicável (hover/cursor pointer). */
  button?: boolean;
  /** Reduz a altura do item; herda do List se omitido. */
  dense?: boolean;
  /** Remove o padding horizontal (gutters). */
  disableGutters?: boolean;
  /** Adiciona borda inferior. */
  divider?: boolean;
  /** Item selecionado (background azul claro). */
  selected?: boolean;
  /** Item desabilitado. */
  disabled?: boolean;
  /** Alinhamento vertical do conteúdo. */
  alignItems?: "flex-start" | "center";
  autoFocus?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  children?: React.ReactNode;
}

const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>((props, ref) => {
  const list = React.useContext(ListContext);

  const {
    className,
    component,
    button = false,
    dense: denseProp,
    disableGutters = false,
    divider = false,
    selected = false,
    disabled = false,
    alignItems = "center",
    autoFocus = false,
    onClick,
    children,
    ...other
  } = props;

  const dense = denseProp ?? list.dense;

  // MUI v4: button=true muda o tag para "div" (não "button") porque <li> não pode ter
  // descendente <button>; usa role="button" + listeners de teclado.
  const Component = component ?? "li";
  const isInteractive = button && !disabled;

  const innerRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (autoFocus && innerRef.current) {
      innerRef.current.focus();
    }
  }, [autoFocus]);

  const setRef = (node: HTMLElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node as HTMLLIElement);
    else if (ref) (ref as React.MutableRefObject<HTMLLIElement | null>).current = node as HTMLLIElement;
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isInteractive) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.(event as unknown as React.MouseEvent);
    }
  };

  const itemCtx = React.useMemo<ListItemContextValue>(() => ({
    dense,
    inset: false,
  }), [dense]);

  return (
    <ListItemContext.Provider value={itemCtx}>
      <Component
        ref={setRef}
        tabIndex={isInteractive ? 0 : undefined}
        role={isInteractive ? "button" : undefined}
        aria-disabled={disabled || undefined}
        onClick={isInteractive ? onClick : undefined}
        onKeyDown={handleKeyDown}
        className={cn(
          // MUI v4: flex horizontal, padding 8px 16px (dense: 4px 16px), text-align left.
          "relative flex w-full text-left box-border",
          alignItems === "flex-start" ? "items-start" : "items-center",
          !disableGutters && "px-4",

          dense ? "py-1" : "py-2",

          divider && "border-b border-slate-200",

          isInteractive && "cursor-pointer transition-colors hover:bg-black/[0.04] focus-visible:bg-black/[0.08] outline-none",

          selected && "bg-primary/10 hover:bg-primary/20",

          disabled && "opacity-50 pointer-events-none",

          className
        )}
        {...other}
      >
        {children}
      </Component>
    </ListItemContext.Provider>
  );
});

ListItem.displayName = "ListItem";

export default ListItem;
